import React, { useState, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { media } from '../theme/tokens';
import { KpiStrip } from '../components/KpiStrip';
import { MetricCard } from '../components/MetricCard';
import {
  SelectDropdown,
  MultiSelectDropdown
} from '../components/SelectDropdown';
import type { SelectOption } from '../components/SelectDropdown';
import { PeriodToggle } from '../components/PeriodToggle';
import { DataUnavailable } from '../components/DataUnavailable';
import { PageHeader } from '../components/PageHeader';
import { TabBar } from '../components/TabBar';
import { InlineBar } from '../components/InlineBar';
import { useAuctionData } from '../../apiTyped/hooks/auction.query';

// ─── Types ───────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'breakdown';

type ViewMode = 'value' | 'volume';
type FormatFilter = 'all' | '750';

// ─── Component ───────────────────────────────────────────────────────────────

export const AuctionSales: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [viewMode, setViewMode] = useState<ViewMode>('value');
  const [formatFilter, setFormatFilter] = useState<FormatFilter>('all');
  const [selectedWine, setSelectedWine] = useState<string>('');
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');

  const queryOptions = useMemo(
    () => ({
      wines: selectedWine || undefined,
      store: selectedStore || undefined,
      market: selectedMarket || undefined
    }),
    [selectedWine, selectedStore, selectedMarket]
  );

  const { isLoading, auctionData } = useAuctionData(queryOptions);

  const { avg_prices, volumes_and_values, filters, feed } = auctionData;
  const kpis = auctionData.kpis as any;

  // Build filter options from API response
  const wineOptions: SelectOption[] = useMemo(
    () =>
      (filters.client_wines || []).map((w: any) => ({
        value: w.name,
        label: w.name
      })),
    [filters.client_wines]
  );
  const storeOptions: SelectOption[] = useMemo(
    () =>
      (filters.stores || []).map((s: any) => ({
        value: s.name,
        label: s.name
      })),
    [filters.stores]
  );
  const marketOptions: SelectOption[] = useMemo(
    () =>
      (filters.markets || []).map((m: any) => ({
        value: m.name,
        label: m.name
      })),
    [filters.markets]
  );

  // Compute filtered volumes_and_values
  const filteredData = useMemo(() => {
    let data = [...volumes_and_values];
    if (formatFilter === '750') {
      data = data.filter((item: any) => Number(item.bottle_format) === 750);
    }
    return data;
  }, [volumes_and_values, formatFilter]);

  // Compute pie chart / breakdown sums
  const breakdownByStore = useMemo(() => {
    const sums: Record<string, { bottles: number; value: number }> = {};
    filteredData.forEach((item: any) => {
      if (!sums[item.store_name]) {
        sums[item.store_name] = { bottles: 0, value: 0 };
      }
      sums[item.store_name].bottles += item.quantity || 0;
      sums[item.store_name].value += item.price || 0;
    });
    return Object.entries(sums)
      .map(([name, { bottles, value }]) => ({ name, bottles, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredData]);

  const breakdownByMarket = useMemo(() => {
    const sums: Record<string, { bottles: number; value: number }> = {};
    filteredData.forEach((item: any) => {
      if (!sums[item.market_name]) {
        sums[item.market_name] = { bottles: 0, value: 0 };
      }
      sums[item.market_name].bottles += item.quantity || 0;
      sums[item.market_name].value += item.price || 0;
    });
    return Object.entries(sums)
      .map(([name, { bottles, value }]) => ({ name, bottles, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredData]);

  const totalValue = breakdownByStore.reduce((sum, s) => sum + s.value, 0);
  const totalBottles = breakdownByStore.reduce((sum, s) => sum + s.bottles, 0);

  const currency = kpis.displayed_currency || 'EUR';

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('fr-FR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Auction Sales"
        subtitle="Track your wines' performance at auction houses — prices, volumes, and market breakdown."
      />

      {/* Filters */}
      <FiltersRow>
        <FilterGroup>
          <FilterLabel>Wine</FilterLabel>
          <SelectDropdown
            options={[{ value: '', label: 'All wines' }, ...wineOptions]}
            value={selectedWine}
            onChange={(val) => setSelectedWine(val)}
            placeholder="All wines"
          />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Auction house</FilterLabel>
          <SelectDropdown
            options={[{ value: '', label: 'All houses' }, ...storeOptions]}
            value={selectedStore}
            onChange={(val) => setSelectedStore(val)}
            placeholder="All houses"
          />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Market</FilterLabel>
          <SelectDropdown
            options={[{ value: '', label: 'All markets' }, ...marketOptions]}
            value={selectedMarket}
            onChange={(val) => setSelectedMarket(val)}
            placeholder="All markets"
          />
        </FilterGroup>
      </FiltersRow>

      {/* Tabs */}
      <TabBar
        tabs={[
          {
            id: 'overview',
            label: 'Overview',
            icon: (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 3v18h18" />
                <rect x="7" y="13" width="3" height="5" />
                <rect x="13" y="9" width="3" height="9" />
                <rect x="19" y="5" width="3" height="13" />
              </svg>
            )
          },
          {
            id: 'breakdown',
            label: 'Breakdown',
            icon: (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a10 10 0 0 1 0 20" />
                <path d="M12 12L12 2" />
                <path d="M12 12L20.5 16.5" />
              </svg>
            )
          }
        ]}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as TabId)}
        meta={
          feed.length > 0 ? <>{feed.length} recent auction events</> : undefined
        }
      />

      {isLoading && <LoadingOverlay>Loading auction data…</LoadingOverlay>}

      {!isLoading && !kpis.wine_name && volumes_and_values.length === 0 && (
        <DataUnavailable unavailable message="Auction Sales data not available">
          <div />
        </DataUnavailable>
      )}

      {/* TAB 1: OVERVIEW */}
      {!isLoading &&
        activeTab === 'overview' &&
        (kpis.wine_name || volumes_and_values.length > 0) && (
          <>
            <KpiStrip
              items={[
                {
                  label: 'Lowest auction price · 750ml',
                  value: kpis.min_price ? formatCurrency(kpis.min_price) : '—',
                  delta: kpis.min_store
                    ? {
                        value: `${kpis.min_store} · ${kpis.min_vintage}`,
                        direction: 'flat' as const
                      }
                    : undefined
                },
                {
                  label: 'Average auction price',
                  value: kpis.avg_price ? formatCurrency(kpis.avg_price) : '—',
                  delta: {
                    value: '750ml incl. tax',
                    direction: 'flat' as const
                  }
                },
                {
                  label: 'Total bottles sold',
                  value: kpis.total_bottles
                    ? kpis.total_bottles.toLocaleString()
                    : '0'
                },
                {
                  label: 'Total sales',
                  value: kpis.total_sales
                    ? kpis.total_sales.toLocaleString()
                    : '0'
                }
              ]}
            />

            {/* Average prices by vintage */}
            {avg_prices.length > 0 && (
              <Section>
                <SectionHeader>
                  <SectionTitle>Average prices by vintage</SectionTitle>
                  <SectionMeta>
                    {avg_prices.length} vintages tracked
                  </SectionMeta>
                </SectionHeader>
                <BarChartWrap>
                  {avg_prices.map((item: any, idx: number) => {
                    const maxPrice = Math.max(
                      ...avg_prices.map((p: any) => p.avg_price || 0)
                    );
                    const height =
                      maxPrice > 0
                        ? ((item.avg_price || 0) / maxPrice) * 100
                        : 0;
                    return (
                      <BarCol key={idx}>
                        <BarValue>
                          {formatCurrency(item.avg_price || 0)}
                        </BarValue>
                        <Bar
                          $height={height}
                          $highlighted={idx === avg_prices.length - 1}
                        />
                        <BarLabel>{item.bottle_vintage}</BarLabel>
                      </BarCol>
                    );
                  })}
                </BarChartWrap>
              </Section>
            )}

            {/* Recent feed */}
            {feed.length > 0 && (
              <Section>
                <SectionHeader>
                  <SectionTitle>Recent activity</SectionTitle>
                </SectionHeader>
                <FeedGrid>
                  {feed.slice(0, 6).map((item: any, idx: number) => (
                    <FeedCard key={idx}>
                      <FeedWine>
                        {item.wine_name || item.name || 'Wine'}
                      </FeedWine>
                      <FeedDetails>
                        {item.store_name && (
                          <FeedDetail>{item.store_name}</FeedDetail>
                        )}
                        {item.market_name && (
                          <FeedDetail>{item.market_name}</FeedDetail>
                        )}
                        {item.price != null && (
                          <FeedPrice>{formatCurrency(item.price)}</FeedPrice>
                        )}
                      </FeedDetails>
                    </FeedCard>
                  ))}
                </FeedGrid>
              </Section>
            )}
          </>
        )}

      {/* TAB 2: BREAKDOWN */}
      {!isLoading && activeTab === 'breakdown' && (
        <>
          {/* View controls */}
          <ControlsRow>
            <PeriodToggle
              options={[
                { label: 'Value', value: 'value' },
                { label: 'Volume', value: 'volume' }
              ]}
              value={viewMode}
              onChange={(v) => setViewMode(v as ViewMode)}
            />
            <PeriodToggle
              options={[
                { label: 'All formats', value: 'all' },
                { label: '750ml only', value: '750' }
              ]}
              value={formatFilter}
              onChange={(v) => setFormatFilter(v as FormatFilter)}
            />
          </ControlsRow>

          <KpiStrip
            items={[
              { label: 'Total value', value: formatCurrency(totalValue) },
              { label: 'Total bottles', value: totalBottles.toLocaleString() },
              {
                label: 'Auction houses',
                value: String(breakdownByStore.length)
              },
              { label: 'Markets', value: String(breakdownByMarket.length) }
            ]}
          />

          {/* By auction house */}
          <Section>
            <SectionHeader>
              <SectionTitle>By auction house</SectionTitle>
            </SectionHeader>
            <TableWrap>
              <StyledTable>
                <thead>
                  <tr>
                    <th>House</th>
                    <th>{viewMode === 'value' ? 'Value' : 'Bottles'}</th>
                    <th>Share</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {breakdownByStore.map((row) => {
                    const metric =
                      viewMode === 'value' ? row.value : row.bottles;
                    const total =
                      viewMode === 'value' ? totalValue : totalBottles;
                    const pct = total > 0 ? (metric / total) * 100 : 0;
                    return (
                      <tr key={row.name}>
                        <td>
                          <StoreName>{row.name}</StoreName>
                        </td>
                        <td>
                          {viewMode === 'value'
                            ? formatCurrency(metric)
                            : `${metric.toLocaleString()} bt.`}
                        </td>
                        <td>
                          <InlineBar percent={pct} />
                        </td>
                        <td></td>
                      </tr>
                    );
                  })}
                </tbody>
              </StyledTable>
            </TableWrap>
          </Section>

          {/* By market */}
          <Section>
            <SectionHeader>
              <SectionTitle>By market</SectionTitle>
            </SectionHeader>
            <TableWrap>
              <StyledTable>
                <thead>
                  <tr>
                    <th>Market</th>
                    <th>{viewMode === 'value' ? 'Value' : 'Bottles'}</th>
                    <th>Share</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {breakdownByMarket.map((row) => {
                    const metric =
                      viewMode === 'value' ? row.value : row.bottles;
                    const total =
                      viewMode === 'value' ? totalValue : totalBottles;
                    const pct = total > 0 ? (metric / total) * 100 : 0;
                    return (
                      <tr key={row.name}>
                        <td>
                          <StoreName>{row.name}</StoreName>
                        </td>
                        <td>
                          {viewMode === 'value'
                            ? formatCurrency(metric)
                            : `${metric.toLocaleString()} bt.`}
                        </td>
                        <td>
                          <InlineBar percent={pct} />
                        </td>
                        <td></td>
                      </tr>
                    );
                  })}
                </tbody>
              </StyledTable>
            </TableWrap>
          </Section>
        </>
      )}
    </Page>
  );
};

// ─── Styled Components ───────────────────────────────────────────────────────

const Page = styled.div`
  padding: 0;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;
`;

const FilterLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--ink-muted);
  font-weight: 600;
`;

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  font-size: 14px;
  color: var(--ink-muted);
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  color: var(--ink);
`;

const SectionMeta = styled.span`
  font-size: 12px;
  color: var(--ink-muted);
`;

// Bar chart
const BarChartWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 200px;
  padding: 16px 0;
  overflow-x: auto;
`;

const BarCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 48px;
  flex: 1;
`;

const BarValue = styled.span`
  font-size: 10px;
  color: var(--ink-muted);
  white-space: nowrap;
`;

const Bar = styled.div<{ $height: number; $highlighted: boolean }>`
  width: 100%;
  max-width: 36px;
  height: ${({ $height }) => $height}%;
  min-height: 4px;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  background: ${({ $highlighted }) =>
    $highlighted ? 'var(--ink)' : 'var(--line-strong)'};
  transition: height 0.3s ease;
`;

const BarLabel = styled.span`
  font-size: 11px;
  color: var(--ink-muted);
  font-weight: 500;
`;

// Feed
const FeedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
`;

const FeedCard = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 14px 16px;
`;

const FeedWine = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: var(--ink);
  margin-bottom: 6px;
`;

const FeedDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const FeedDetail = styled.span`
  font-size: 12px;
  color: var(--ink-muted);
`;

const FeedPrice = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
`;

// Controls
const ControlsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

// Tables
const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: 10px 14px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--ink-muted);
    font-weight: 600;
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid var(--line-light, var(--line));
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: var(--bg-hover, #fafafa);
  }
`;

const StoreName = styled.span`
  font-weight: 500;
  color: var(--ink);
`;
